import { trackFrameGift, trackFrameGiftSuccess, type FrameGiftOffer } from '@src/utils/frameGiftAnalytics';
import { ThemeArtwork } from '@src/theme/Artwork';
import './PersonalGiftCheckout.css';
import { GifteeFramePreview } from './full/GifteeFramePreview';
import { showDialog } from '@src/components';
import { closeAllDialogs, setDialogTitle } from '@src/components/Dialog/Dialog';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, GiftIcon, Hint, Link, Typography, closeDialog } from '@src/components';
import { EmailInput } from '@src/components/EmailInput/EmailInput';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';
import { loadBox } from '@src/state/boxSlice';
import { isDotCom } from '@src/site';
import { hasAdFreeAccess } from '@src/components/Ads/eligibility';
import { santaClient } from '@src/state/client';

export function PersonalGiftCheckout({
  boxKey,
  flowId: initialFlowId,
  wardId,
  message = '',
  payment: initialPayment,
  children,
  initialEmail,
  paymentFailed = false,
}: {
  boxKey?: string;
  flowId?: string;
  initialEmail?: string;
  paymentFailed?: boolean;
  children?: ReactNode | ((offer: FrameGiftOffer | null) => ReactNode);
  wardId?: number;
  message?: string;
  payment?: number;
}) {
  const { t, i18n } = useTranslation();
  const [flowId] = useState(() => initialFlowId ?? crypto.randomUUID());
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const finish = () => {
    closeAllDialogs();
    if (/\/gifted$/.test(window.location.pathname))
      navigate(`/box/${encodeURIComponent(boxKey ?? '')}`, { replace: true });
  };
  const user = useAppSelector((s) => s.session.user);
  const boxInfo = useAppSelector((s) => s.box.info);
  // Capture access before this gift grants ad-free access and refreshes the session.
  const [alreadyAdFree] = useState(() =>
    isDotCom() || hasAdFreeAccess(user) ||
    (boxInfo?.box.key === boxKey && hasAdFreeAccess(boxInfo?.box)),
  );
  const [showAdsThankYou, setShowAdsThankYou] = useState(false);
  const userId = user?.id;
  useEffect(() => {
    if (alreadyAdFree) return;
    let active = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Anonymous checkout skips the external donation-status request.
    if (!userId) setShowAdsThankYou(true);
    else void santaClient.getIsDonated().then(result => {
      if (active) setShowAdsThankYou(result.ok && result.data === false);
    }).catch(() => { /* Do not promise a new benefit when prior access is unknown. */ });
    return () => { active = false; };
  }, [alreadyAdFree, userId]);
  const [email, setEmail] = useState(initialEmail ?? user?.email ?? '');
  const [offer, setOffer] = useState<FrameGiftOffer & {
    code: string;
    amountMinor: number;
    currency: string;
    available: boolean;
    checkoutAvailable: boolean;
  } | null>(null);
  const [payment, setPayment] = useState(initialPayment ?? null);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (sent) setDialogTitle(t('boxGift.sentTitle'));
  }, [sent, t]);
  const checkoutViewed = useRef(false);
  useEffect(() => {
    if (boxKey && offer && !initialPayment && !checkoutViewed.current) {
      checkoutViewed.current = true;
      trackFrameGift('gift_frame_checkout_viewed', { flow_id: flowId, test_price: offer.testPrice }, offer);
    }
  }, [boxKey, initialPayment, flowId, offer]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(paymentFailed ? t('boxGift.checkoutFailed') : '');
  const inFlight = useRef(false);
  const emailContainer = useRef<HTMLDivElement>(null);
  const request = useRef<{ body: string; id: string } | null>(null);
  const endpoint = `/api/box/${encodeURIComponent(boxKey ?? '')}/gift`;
  const [testPriceQuery] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    const query = new URLSearchParams();
    if (search.has('price')) {
      query.set('price', search.get('price')!);
    }
    return query.size ? `?${query}` : '';
  });
  useEffect(() => {
    let active = true;
    if (!initialPayment && boxKey)
      void fetch(`${endpoint}/personal-offer${testPriceQuery}`, { credentials: 'same-origin' })
        .then(async (r) => {
          if (!r.ok) throw Error();
          return r.json();
        })
        .then((data) => {
          if (active) setOffer(data);
        })
        .catch(() => {
          if (active) setError(t('boxGift.checkoutUnavailable'));
        });
    return () => {
      active = false;
    };
  }, [endpoint, initialPayment, boxKey, testPriceQuery, t]);
  useEffect(() => {
    if (!payment || !boxKey) return;
    let active = true,
      checking = false;
    const check = async () => {
      if (checking) return;
      checking = true;
      try {
        const r = await fetch(`${endpoint}/payment?payment=${payment}`, {
          credentials: 'same-origin',
        });
        if (!r.ok) throw Error();
        const result = await r.json();
        if (!active) return;
        setError('');
        if (result.status === 'fulfilled' && result.personal) {
          trackFrameGiftSuccess(payment, result.offer);
          setSent(true);
          if (/\/gifted$/.test(window.location.pathname)) {
            const search = new URLSearchParams(window.location.search);
            ['personal', 'result', 'payment'].forEach(key => search.delete(key));
            const query = search.toString();
            navigate(`/box/${encodeURIComponent(boxKey)}${query ? `?${query}` : ''}`, { replace: true });
          }
          setPayment(null);
          setError('');
          void dispatch(loadBox({ key: boxKey }));
          void dispatch(loadSession());
        } else if (['failed', 'cancelled'].includes(result.status)) {
          if (initialPayment) {
            // Never replace a saved message with an empty retry form if recovery fails.
            if (!result.personal || typeof result.draft?.message !== 'string') throw Error();
            const wardResponse = await fetch(`/api/box/${encodeURIComponent(boxKey)}/ward`, { credentials: 'same-origin', cache: 'no-store' });
            if (!wardResponse.ok) throw Error();
            const ward = await wardResponse.json();
            if (!active) return;
            const search = new URLSearchParams(window.location.search);
            ['personal', 'result', 'payment'].forEach(key => search.delete(key));
            navigate(`/box/${encodeURIComponent(boxKey)}/ward${search.size ? `?${search}` : ''}`, { replace: true });
            showDialog({title:t('game.ward.framePreviewTitle'),mobileFullScreen:true,testId:'giftee-frame-dialog',children:<GifteeFramePreview ward={ward} boxKey={boxKey} initialMessage={result.draft?.message ?? ''} initialEmail={result.draft?.receiptEmail} paymentFailed />});
          }
          setPayment(null);
          setError(t('boxGift.checkoutFailed'));
          request.current = null;
        }
      } catch {
        if (active) setError(t('boxGift.paymentCheckError'));
      } finally {
        checking = false;
      }
    };
    void check();
    const timer = window.setInterval(check, 3000);
    window.addEventListener('focus', check);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [payment, endpoint, boxKey, dispatch, navigate, t]);
  async function buy() {
    if (!boxKey || inFlight.current || !offer?.available || !offer.checkoutAvailable) return;
    if (!emailContainer.current?.querySelector('input')?.reportValidity()) return;
    const messageType = !message.trim() ? 'empty' : ['warmth','smile','magic','santa'].some(key => t('game.ward.frameMessages.' + key) === message.trim()) ? 'predefined' : 'custom';
    trackFrameGift('gift_frame_checkout_clicked', { message_type: messageType, test_price: offer.testPrice, flow_id: flowId }, offer);
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      trackFrameGift('gift_frame_checkout_failed', { test_price: offer.testPrice }, offer);
      setError(t('boxGift.popupBlocked'));
      return;
    }
    tab.opener = null;
    tab.document.title = t('boxGift.preparingCheckout');
    tab.document.body.textContent = t('boxGift.preparingCheckout');
    inFlight.current = true;
    setBusy(true);
    setError('');
    const body = JSON.stringify({
      offerCode: offer.code,
      wardId,
      message: message.trim(),
      giftAnonymously: true,
      parentGiftId: 0,
      receiptEmail: email.trim(),
    });
    if (request.current?.body !== body + testPriceQuery) request.current = { body: body + testPriceQuery, id: crypto.randomUUID() };
    try {
      const r = await fetch(`${endpoint}/checkout${testPriceQuery}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...JSON.parse(body), requestId: request.current.id }),
      });
      if (!r.ok) throw Error();
      const data = await r.json();
      trackFrameGift('gift_frame_checkout_started', { message_type: messageType, test_price: offer.testPrice, flow_id: flowId, request_id: request.current!.id }, offer);
      setPayment(data.id);
      tab.location.href = data.paymentUrl;
    } catch {
      trackFrameGift('gift_frame_checkout_failed', { test_price: offer.testPrice }, offer);
      tab.close();
      setError(t('boxGift.checkoutFailed'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return (
    <div className="giftee-frame-preview" data-testid="personal-gift-checkout">
      {sent ? (
        <>
          <ThemeArtwork role="gift" className="personal-gift-checkout__success-gift" />
          <Typography as="p" role="body/base">
            {t('game.ward.personalGiftGenerosity')}
          </Typography>
          {showAdsThankYou && (
            <Typography as="p" role="body/sm" data-testid="personal-gift-ads-thanks">
              {t('game.ward.personalGiftSenderAds')}
            </Typography>
          )}
          <Button fullWidth onClick={finish}>
            {t('game.ward.personalGiftBack')}
          </Button>
        </>
      ) : payment ? (
        <>
          <Typography as="p" role="body/base">
            {t('boxGift.paymentTitle')}
          </Typography>
          <Typography as="p" role="body/sm">
            {t('game.ward.personalGiftPaymentWait')}
          </Typography>
          <Link variant="quiet" onClick={finish}>
            {t('game.ward.personalGiftBack')}
          </Link>
        </>
      ) : initialPayment ? null : (
        <>
        {(!boxKey || offer) && (typeof children === 'function' ? children(offer) : children)}
        <div className="giftee-frame-preview" ref={emailContainer}>
          <EmailInput
            name="receiptEmail"
            testId="personal-gift-receipt-email"
            label={t('boxGift.receiptEmail')}
            value={email}
            onValueChange={setEmail}
            required
          />
          {error && <Hint tone="error">{error}</Hint>}
          <Button
            fullWidth
            type="button"
            onClick={() => void buy()}
            loading={busy}
            disabled={!!boxKey && (!offer?.available || !offer?.checkoutAvailable)}
            icon={<GiftIcon />}
            testId="personal-gift-pay"
          >
            {t('game.ward.personalGiftPay', {
              price: offer
                ? new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency: offer.currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  }).format(offer.amountMinor / 100)
                : boxKey ? '…' : isDotCom() ? '$2' : '99 ₽',
            })}
          </Button>
        </div>
        </>
      )}
      {!sent && !payment && (
        <Link variant="quiet" onClick={initialPayment ? finish : closeDialog}>
          {t(initialPayment ? 'game.ward.personalGiftBack' : 'game.ward.framePreviewClose')}
        </Link>
      )}
      {error && (payment || initialPayment) && <Hint tone="error">{error}</Hint>}
    </div>
  );
}
